import { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import Toast from '../../components/Toast'

import { API__PRODUCTS, API__USER } from '../../api'
import { USER__SET_SUBSCRIPTION, USER__SET_SUBSCRIPTIONS } from '../../store/actions'

import useDowngradeModal from './components/DowngradeModal'
import { useMerchantCode, useDowngradeAction } from './hooks'
import { EDUCATION_PRODUCT_LIST, PRODUCT_LIST } from '../../common/constants/ProductConstants'

export const useSubscriptionActions = (type, t) => {
    const { user_details, user_subscription, language, products } = useSelector(state => state)
    const dispatch = useDispatch()
    const merchantCode = useMerchantCode()
    const [actionsMap, setActionsMap] = useState(null)

    const { modal, setOpen, setDowngradeAction, setDowngradeTo } = useDowngradeModal()

    const downgradeAction = useDowngradeAction()

    useEffect(() => {
        const fetch = async () => {
            const rules = await API__PRODUCTS.GET_PRODUCT_CHANGED_RULES()

            const mapped = Object.fromEntries(
                Object.entries(rules).map(([key, val]) => [
                    key,
                    Object.fromEntries(val.map(obj => Object.entries(obj)[0])),
                ]),
            )

            setActionsMap(mapped)
        }

        if (actionsMap === null) fetch()
    }, [])

    if (!products || !merchantCode || !actionsMap) {
        return [null, null]
    }

    const paymentArgs = () =>
        user_subscription.lastPayment
            ? `&CURRENCY=${'USD'}` + `&PAY_TYPE=${user_subscription.lastPayment.paymentMethodCode}`
            : ''

    const avCustomerIdForBuy = subscriptionReference => {
        if (subscriptionReference && subscriptionReference.length > 0) {
            return `&AV_CUSTOMERID=${user_details.avangateCustomerReference}`
        }
        return ''
    }

    const buyLinkTemplate = ({ productId, productCode, guid }) =>
        'https://secure.2checkout.com/order/pf.php' +
        `?MERCHANT=${merchantCode}` +
        `&BILL_EMAIL=${user_details.email}` +
        `&BILL_FNAME=${user_details.first_name || ''}` +
        `&BILL_LNAME=${user_details.last_name || ''}` +
        '&URL=' +
        encodeURIComponent(
            'https://secure.2checkout.com/order/checkout.php' +
                `?PRODS=${productId}` +
                `&CUSTOMERID=${user_details.id}` +
                `${avCustomerIdForBuy(user_subscription.subscriptionReference)}` +
                `&LANG=${language}` +
                '&QTY=1' +
                `&ADDITIONAL_order_guid=${guid}` +
                `&ADDITIONAL_s_type=${process.env.REACT_APP_ENVIRONMENT}` +
                `&ADDITIONAL_license_ref=${user_subscription.subscriptionReference}` +
                `&ADDITIONAL_order_type=${actionsMap[user_subscription.product.productCode][productCode]}` +
                paymentArgs() +
                `&BACK_REF=${encodeURIComponent(
                    `${
                        process.env.REACT_APP_STATIC_URL + window.location.pathname + '?tab=subscription'
                    }&order_guid=${guid}`,
                )}`,
        )
            .replace(/'/g, '%27')
            .replace(/"/g, '%22')

    const upgradeLinkTemplate = ({ productId, productCode, guid }) =>
        'https://secure.2checkout.com/order/pf.php' +
        `?MERCHANT=${merchantCode}` +
        `&BILL_EMAIL=${user_details.email}` +
        `&BILL_FNAME=${user_details.first_name || ''}` +
        `&BILL_LNAME=${user_details.last_name || ''}` +
        '&URL=' +
        encodeURIComponent(
            'https://secure.2checkout.com/order/upgrade.php' +
                `?PRODS=${user_subscription.product.productId}` +
                `&UPGRADEPROD=${productId}` +
                `&CUSTOMERID=${user_details.id}` +
                `&AV_CUSTOMERID=${user_details.avangateCustomerReference}` +
                `&LICENSE=${user_subscription.subscriptionReference}` +
                `&LANG=${language}` +
                `&QTY=1` +
                `&ADDITIONAL_order_guid=${guid}` +
                `&ADDITIONAL_s_type=${process.env.REACT_APP_ENVIRONMENT}` +
                `&ADDITIONAL_license_ref=${user_subscription.subscriptionReference}` +
                `&ADDITIONAL_order_type=${actionsMap[user_subscription.product.productCode][productCode]}` +
                paymentArgs() +
                `&BACK_REF=${encodeURIComponent(
                    `${
                        process.env.REACT_APP_STATIC_URL + window.location.pathname + '?tab=subscription'
                    }&order_guid=${guid}`,
                )}`,
        )
            .replace(/'/g, '%27')
            .replace(/"/g, '%22')

    const getAction = (name, item) => {
        const map = {
            buy: item => () => window.open(buyLinkTemplate(item)),
            upgrade: item => () =>
                window.open((user_subscription.subscriptionReference ? upgradeLinkTemplate : buyLinkTemplate)(item)),
        }

        return async () => {
            const guid = uuidv4()
            const action = map[name]({ ...item, guid })

            const current_subscription = await API__USER.GET_SELECTED_SUBSCRIPTION(user_details.id)

            if (user_subscription.product.productCode === current_subscription.product.productCode) {
                action()
            } else {
                Toast('success', {
                    title: t('_common._notify._success.title'),
                    msg: t('_pages._billing_and_payments.subsciptions_changed'),
                })

                dispatch(USER__SET_SUBSCRIPTION(current_subscription))
                dispatch(USER__SET_SUBSCRIPTIONS(await API__USER.GET_SUBSCRIPTIONS(user_details.id)))
            }
        }
    }

    const productsByType = products[type]

    const actionByCode = actionsMap[user_subscription.product.productCode]

    const actions = Object.fromEntries(
        productsByType.map(item => {
            const { name, productCode } = item

            if (productCode === user_subscription.product.productCode) {
                return [name, { type: 'current', description: t('_pages._billing_and_payments.current_plan') }]
            }

            const actionData = {
                upgrade: {
                    type: 'upgrade',
                    description: [
                        PRODUCT_LIST.Free,
                        PRODUCT_LIST.TrialEnded,
                        PRODUCT_LIST.Trial,
                        EDUCATION_PRODUCT_LIST.EducationFree,
                    ].includes(user_subscription.product.productCode)
                        ? t('_common.buy')
                        : t('_common.upgrade'),
                    action: getAction('upgrade', item),
                },
                buy: { type: 'upgrade', description: t('_common.buy'), action: getAction('buy', item) },
                downgrade: {
                    type: 'downgrade',
                    description: t('Downgrade'),
                    action: () => {
                        setDowngradeTo(item.name)
                        setDowngradeAction(() => downgradeAction(item))
                        setOpen(true)
                    },
                },
                not_available: { type: 'notavailable' },
            }
            return [name, actionData[actionByCode[productCode]]]
        }),
    )

    return [modal, actions]
}

/**
 * From https://stackoverflow.com/questions/105034/create-guid-uuid-in-javascript
 */
export const uuidv4 = () => {
    return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
        (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16),
    )
}
